'use client';

import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface OtpModalProps {
  open: boolean;
  phone: string;
  onClose: () => void;
  onVerified: () => void;
  isSeller?: boolean;
  isBuyerRegister?: boolean;
  isSellerRegister?: boolean;
  buyerName?: string;
  sellerName?: string;
}

export default function OtpModal({
  open,
  phone,
  onClose,
  onVerified,
  isSeller = false,
  isBuyerRegister = false,
  isSellerRegister = false,
  buyerName = '',
  sellerName = '',
}: OtpModalProps) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [error, setError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { verifyOtp, registerBuyer, registerSeller, signInWithPhone, user } = useAuth();

  useEffect(() => {
    if (open) {
      setResendTimer(30);
      setOtp(['', '', '', '', '', '']);
      setError('');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer, open]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
    }
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < 6) {
      setError('Enter the complete 6-digit OTP');
      return;
    }
    setIsVerifying(true);
    setError('');
    try {
      const data = await verifyOtp(phone, code);
      const userId = data?.user?.id;

      // After OTP verified, update profile if registering
      if (userId) {
        if (isBuyerRegister && buyerName) {
          await registerBuyer(userId, buyerName, phone);
        } else if (isSellerRegister && sellerName) {
          await registerSeller(userId, sellerName, phone);
        }
      }

      onVerified();
    } catch (err: any) {
      const msg = err?.message || 'Incorrect OTP. Please try again.';
      setError(msg.includes('expired') ? 'OTP has expired. Please request a new one.' : 'Incorrect OTP. Please try again.');
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    try {
      await signInWithPhone(phone);
      toast.success(`OTP resent to +91 ${phone}`);
      setResendTimer(30);
      setOtp(['', '', '', '', '', '']);
      setError('');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resend OTP');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Verify Your Mobile Number"
      description={`We've sent a 6-digit OTP to +91 ${phone || 'XXXXXXXXXX'}`}
      size="sm"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-600 text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            disabled={isVerifying || otp.join('').length < 6}
            className="px-5 py-2 rounded-lg text-sm font-700 btn-primary flex items-center gap-2"
            style={{ minWidth: '120px', justifyContent: 'center' }}
          >
            {isVerifying ? (
              <>
                <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              'Verify OTP'
            )}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2 justify-center" onPaste={handlePaste}>
          {otp.map((digit, i) => (
            <input
              key={`otp-${i + 1}`}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className={`w-11 h-12 text-center text-lg font-700 rounded-lg border-2 transition-all outline-none ${
                digit
                  ? 'border-primary bg-primary-light text-primary' :'border-border bg-card text-foreground'
              } focus:border-primary focus:shadow-[0_0_0_3px_rgba(22,163,74,0.12)]`}
              aria-label={`OTP digit ${i + 1}`}
            />
          ))}
        </div>

        {error && (
          <p className="text-xs text-negative text-center">{error}</p>
        )}

        <div className="text-center">
          {resendTimer > 0 ? (
            <p className="text-sm text-muted-foreground">
              Resend OTP in <span className="font-700 text-foreground tabular-nums">{resendTimer}s</span>
            </p>
          ) : (
            <button
              onClick={handleResend}
              className="text-sm font-600 text-primary hover:underline"
            >
              Resend OTP
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}